import { Button, Checkbox, DatePicker, Form, Input, InputNumber, Modal, Select } from 'antd';
import Title from 'antd/es/typography/Title';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { DropdownIconNew } from '../../theme/SvgIcons';
import { FlexWrapper, GreyText, PurpleText } from '../../theme/common_style';
import { toast } from 'react-toastify';
import useProjectSourced from '../../hooks/useProjectSourced';
import { followUp, leadPlateform, realTimeStatusOptions } from '../../utils/constant';
import { AvatarSelect } from '../../components/common/AvatarSelect';
import useGetBusinessEmp from '../../hooks/useGetBusinessEmp';
import { Country } from 'country-state-city';
import styled from '@emotion/styled';
import { CreateFormWrapper, CreateModalWrapper, FieldBox } from '../projects/ProjectStyle';
import { addProposalSheet, updateProposalSheet } from '../../services/api_collection';
import dayjs from 'dayjs';
import { useResponsiveModalWidth } from '../../hooks/useResponsiveModalWidth';

const CreateLeads = ({ open = true, close, editDetails, handleLeadList }) => {
  const allCountries = Country.getAllCountries();
  const [loading, setLoading] = useState(false);
  const [realTimeStatus, setRealTimeStatus] = useState(false);
  const width = useResponsiveModalWidth(1000);

  const [form] = Form.useForm();
  const Option = Select.Option;
  const { loading: businessEmpLoading, options: businessEmpList } = useGetBusinessEmp();

  const type = 'Upwork';
  const { loading: projectSourcedLoading, options: projectSourced } = useProjectSourced({ type });

  const businessManager = useMemo(() => {
    return projectSourced?.filter((emp) => emp?.is_bussiness_manager);
  }, [projectSourced]);

  const onCancel = () => {
    form.resetFields();
    close();
  };

  const handleCreate = async (values) => {
    try {
      setLoading(true);
      const payload = {
        ...values
      };
      if (values?.billing_id_name_id) {
        payload.billing_id_name_id = Number(values?.billing_id_name_id);
      }
      if (values?.business_manager_id) {
        payload.business_manager_id = Number(values?.business_manager_id);
      }

      payload.date = dayjs(values?.date).format('YYYY-MM-DD');

      const Api = editDetails ? updateProposalSheet : addProposalSheet;

      const res = await Api(payload, editDetails?.id);
      if (res?.statusCode === 200) {
        toast.success(res?.message || `Lead ${editDetails ? 'updated' : 'created'} successfully`);
        form.resetFields();
        close();
        handleLeadList();
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error(error?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editDetails) {
      form.setFieldsValue({
        ...editDetails,
        billing_id_name_id: editDetails?.billing_id_name?.id || null,
        business_manager_id: editDetails?.business_manager?.id || null,
        call_id: editDetails?.call?.id || null,
        owner_id: editDetails?.owner?.id || null,
        date: dayjs(editDetails?.date),
        give_estimation: editDetails?.give_estimation ? dayjs(editDetails?.give_estimation) : null,
        get_estimation: editDetails?.get_estimation ? dayjs(editDetails?.get_estimation) : null,
        module_sheet_sended: editDetails?.module_sheet_sent ? editDetails?.module_sheet_sent : false
      });

      if (editDetails?.real_time_status) {
        setRealTimeStatus(
          !realTimeStatusOptions?.some((option) => option.value === editDetails?.real_time_status)
        );
      }
    }
  }, [editDetails]);

  return (
    <Modal
      maskClosable={false}
      open={open}
      onCancel={onCancel}
      centered
      prefixCls="antCustomModal"
      width={width}
      footer={null}>
      <CreateModalWrapper>
        <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
          Create Lead
        </Title>

        <Form autoComplete="off" form={form} onFinish={handleCreate} layout="vertical">
          {() => (
            <>
              <CreateFormWrapper>
                <GridBox cols="3">
                  {/* Date Field */}
                  <FieldBox>
                    <label>
                      Date <span>*</span>
                    </label>
                    <Form.Item
                      name="date"
                      rules={[{ required: true, message: 'Please select date' }]}>
                      <DatePicker
                        prefixCls="form-datepicker"
                        format="YYYY-MM-DD"
                        placeholder="YYYY-MM-DD"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <label>
                      Lead Manager <span>*</span>
                    </label>
                    <Form.Item
                      name="owner_id"
                      rules={[{ required: true, message: 'Lead manager is required' }]}>
                      <AvatarSelect
                        options={businessEmpList}
                        placeholder="Select Lead Manager"
                        loading={businessEmpLoading}
                        isEmp={true}
                      />
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <label>
                      ID Name <span>*</span>
                    </label>
                    <Form.Item
                      name="billing_id_name_id"
                      type="text"
                      rules={[
                        {
                          required: true,
                          message: 'ID Name is required'
                        }
                      ]}>
                      <Select
                        prefixCls="form-select"
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                          (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ marginBottom: '10px' }}
                        suffixIcon={<DropdownIconNew />}
                        placeholder="--Select Option--"
                        loading={projectSourcedLoading}>
                        {projectSourced?.map((role) => (
                          <Option
                            key={role.value}
                            value={role.value}
                            style={{ textTransform: 'capitalize' }}>
                            {role.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <label>
                      Client Name <span>*</span>
                    </label>
                    <Form.Item
                      name="client_name"
                      rules={[{ required: true, message: 'Client name is required' }]}>
                      <Input placeholder="Enter client name" prefixCls="form-input" />
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <label>
                      Job Title <span>*</span>
                    </label>
                    <Form.Item
                      name="job_title"
                      rules={[{ required: true, message: 'Job title is required' }]}>
                      <Input placeholder="Enter job title" prefixCls="form-input" />
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <label>
                      Proposal <span>*</span>
                    </label>
                    <Form.Item
                      name="prop_url"
                      rules={[{ required: true, message: 'Proposal is required' }]}>
                      <Input placeholder="Enter proposal" prefixCls="form-input" />
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <label>
                      Country <span>*</span>
                    </label>
                    <Form.Item
                      name="country"
                      rules={[{ required: true, message: 'Country is required' }]}>
                      <Select
                        prefixCls="form-select"
                        showSearch
                        suffixIcon={<DropdownIconNew />}
                        placeholder="--Select Option--">
                        {allCountries?.map((item) => (
                          <Option
                            key={item.isoCode}
                            value={item.name}
                            style={{ textTransform: 'capitalize' }}>
                            {item.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <label>Business Manager</label>
                    <Form.Item name="business_manager_id">
                      <AvatarSelect
                        options={businessManager}
                        placeholder="Select Lead Manager"
                        loading={businessEmpLoading}
                        isEmp={true}
                      />
                    </Form.Item>
                  </FieldBox>

                  <FieldBox>
                    <label>Call By</label>
                    <Form.Item name="call_id">
                      <AvatarSelect
                        options={businessEmpList}
                        placeholder="Select Lead Manager"
                        loading={businessEmpLoading}
                        isEmp={true}
                      />
                    </Form.Item>
                  </FieldBox>

                  <FieldBox>
                    <label>Estimation</label>
                    <Form.Item name="estimation">
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="Enter Result"
                        min={0}
                        prefixCls="form-input-number"
                        controls={false}
                      />
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <label>Sent mail time for estimation</label>
                    <Form.Item name="give_estimation">
                      <DatePicker
                        showTime={{ format: 'hh:mm A' }}
                        prefixCls="form-datepicker"
                        format="YYYY-MM-DD hh:mm A"
                        placeholder="YYYY-MM-DD hh:mm AM/PM"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <label>Received Estimation time</label>
                    <Form.Item name="get_estimation">
                      <DatePicker
                        showTime={{ format: 'hh:mm A' }}
                        prefixCls="form-datepicker"
                        format="YYYY-MM-DD hh:mm A"
                        placeholder="YYYY-MM-DD hh:mm AM/PM"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </FieldBox>
                </GridBox>

                <GridBox cols={2}>
                  <FieldBox style={{ width: '100%' }}>
                    <label>Plateform</label>
                    <Form.Item name="platform">
                      <Select
                        allowClear
                        mode="multiple"
                        maxTagCount={2}
                        prefixCls="antMultipleSelector"
                        suffixIcon={<DropdownIconNew />}
                        placeholder="--Select Option--"
                        options={leadPlateform}
                      />
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <FlexWrapper justify="space-between" align="center">
                      <label htmlFor="real_time_status">Real Time Status</label>
                      <PurpleText
                        style={{ cursor: 'pointer', width: 'max-content' }}
                        onClick={() => {
                          setRealTimeStatus(!realTimeStatus);
                          form.setFieldsValue({ real_time_status: null });
                        }}>
                        {realTimeStatus ? '- Remove' : '+ Add'}
                      </PurpleText>
                    </FlexWrapper>
                    <Form.Item name="real_time_status">
                      {realTimeStatus ? (
                        <Input
                          style={{ width: '100%' }}
                          prefixCls="form-input"
                          placeholder="Enter real time status"
                        />
                      ) : (
                        <Select
                          allowClear
                          showSearch
                          prefixCls="form-select"
                          suffixIcon={<DropdownIconNew />}
                          placeholder="--Select Option--"
                          filterOption={(input, option) =>
                            (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                          }>
                          {realTimeStatusOptions?.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </FieldBox>
                </GridBox>

                <GridBox cols="2">
                  <FieldBox>
                    <Form.Item
                      name="module_sheet_send"
                      valuePropName="checked"
                      style={{ marginBottom: '0px' }}>
                      <CustomCheckbox>
                        <span style={{ color: '#000' }}>
                          Has the module sheet been sent to the client?
                        </span>
                      </CustomCheckbox>
                    </Form.Item>
                  </FieldBox>
                  <FlexWrapper align="center" gap="10px">
                    <label>Follow Up </label>
                    <Form.Item name="days" style={{ marginBottom: '0px' }}>
                      <VerticalCheckboxGroup>
                        {followUp?.map((el) => (
                          <CustomCheckbox key={el} value={el}>
                            {el}
                          </CustomCheckbox>
                        ))}
                      </VerticalCheckboxGroup>
                    </Form.Item>
                  </FlexWrapper>
                </GridBox>

                <FieldBox>
                  <FlexWrapper justify="space-between">
                    <label>Note</label>
                    <GreyText>Max 1000 characters</GreyText>
                  </FlexWrapper>
                  <Form.Item name="note" type="text" rules={[{ required: false }]}>
                    <Input.TextArea
                      style={{ resize: 'none' }}
                      maxLength={1000}
                      placeholder="Write a note here"
                      rows={5}
                    />
                  </Form.Item>
                </FieldBox>
              </CreateFormWrapper>

              <FlexWrapper justify={'end'} gap={'10px'} style={{ marginTop: '20px' }}>
                <Button
                  loading={loading}
                  onClick={form.submit}
                  style={{ width: '140px' }}
                  prefixCls="antCustomBtn">
                  Save
                </Button>
              </FlexWrapper>
            </>
          )}
        </Form>
      </CreateModalWrapper>
    </Modal>
  );
};

export default CreateLeads;

CreateLeads.propTypes = {
  open: PropTypes.bool,
  close: PropTypes.func,
  handleLeadList: PropTypes.func,
  editDetails: PropTypes.object
};

export const VerticalCheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const CustomCheckbox = styled(Checkbox)`
  text-transform: capitalize !important;
  &.ant-checkbox-wrapper .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${({ theme }) => theme.primaryColor} !important;
    border-color: #ffffff !important;
  }
  &.ant-checkbox-wrapper-disabled .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${({ theme }) => theme.primaryColor} !important;
    border-color: #ffffff !important;
    opacity: 1 !important;
  }
  &.ant-checkbox-wrapper-disabled .ant-checkbox-checked .ant-checkbox-inner:after {
    border-color: #ffffff !important;
  }
`;
const GridBox = styled.div`
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols}, 1fr)`};
  gap: 16px;
  @media (max-width: 850px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 650px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;
